import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";

const inspectionLabel = (inspection) => {
  if (!inspection) return "";
  const number = inspection.inspection_number || `Inspection #${inspection.id}`;
  const type = inspection.inspection_type || "Inspection";
  const location = inspection.site_location || inspection.location;
  const date = inspection.inspection_date;
  return [number, type, location, date].filter(Boolean).join(" • ");
};

function SearchableInspectionSelect({
  inspections,
  loading,
  value,
  onChange,
  disabled,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedId = value ? Number(value) : null;

  useEffect(() => {
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const selected = useMemo(
    () =>
      (inspections || []).find(
        (inspection) => Number(inspection.id) === selectedId,
      ) || null,
    [inspections, selectedId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return inspections || [];
    return (inspections || []).filter((inspection) =>
      [
        inspection.inspection_number,
        inspection.inspection_type,
        inspection.site_location,
        inspection.location,
        inspection.inspection_date,
        inspection.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [inspections, query]);

  return (
    <div ref={rootRef} className="relative mt-1">
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen((current) => !current)}
        className="flex min-h-[42px] w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm disabled:opacity-60"
      >
        <span className={selected ? "truncate text-gray-900" : "text-gray-400"}>
          {loading
            ? "Loading inspections..."
            : selected
              ? inspectionLabel(selected)
              : "Search inspection number, type, location, or date"}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {selected ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                onChange("");
                setQuery("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  onChange("");
                  setQuery("");
                }
              }}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Clear inspection"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : null}
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </span>
      </button>

      {open && !loading ? (
        <div className="absolute z-40 mt-2 w-full overflow-hidden rounded-xl border bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search inspections..."
              className="w-full text-sm outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => {
                onChange("");
                setQuery("");
                setOpen(false);
              }}
              className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50"
            >
              No related inspection
            </button>
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">
                No matching inspections.
              </div>
            ) : (
              filtered.map((inspection) => (
                <button
                  key={inspection.id}
                  type="button"
                  onClick={() => {
                    onChange(String(inspection.id));
                    setQuery("");
                    setOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    Number(inspection.id) === selectedId ? "bg-orange-50" : ""
                  }`}
                >
                  <div className="font-medium text-gray-900">
                    {inspection.inspection_number || `Inspection #${inspection.id}`}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-gray-500">
                    {[
                      inspection.inspection_type,
                      inspection.site_location || inspection.location,
                      inspection.inspection_date,
                      inspection.status,
                    ]
                      .filter(Boolean)
                      .join(" • ") || "No additional inspection details"}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const NCRForm = ({
  value,
  onChange,
  onSubmit,
  onCancel,
  creating,
  inspections = [],
  inspectionsLoading = false,
  mode = "create",
}) => {
  const v = value || {};
  const isEdit = mode === "edit";

  return (
    <form onSubmit={onSubmit} className="rounded-lg bg-white p-6 shadow space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="text-sm text-gray-700">Title</label>
          <input
            value={v.title || ""}
            onChange={(e) => onChange("title", e.target.value)}
            className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
            placeholder="e.g., Rebar spacing non-compliance"
            required
          />
        </div>

        <div>
          <label className="text-sm text-gray-700">Location</label>
          <input
            value={v.location || ""}
            onChange={(e) => onChange("location", e.target.value)}
            className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
            placeholder="e.g., Zone B"
          />
        </div>

        <div>
          <label className="text-sm text-gray-700">Severity</label>
          <select
            value={v.severity || "medium"}
            onChange={(e) => onChange("severity", e.target.value)}
            className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
          >
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
            <option value="critical">critical</option>
          </select>
        </div>

        {!isEdit ? (
          <div>
            <label className="text-sm text-gray-700">Status</label>
            <select
              value={v.status || "open"}
              onChange={(e) => onChange("status", e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
            >
              <option value="open">open</option>
              <option value="in_review">in_review</option>
              <option value="closed">closed</option>
            </select>
          </div>
        ) : null}

        <div className={isEdit ? "md:col-span-2" : ""}>
          <label className="text-sm text-gray-700">
            Related inspection (optional)
          </label>
          <SearchableInspectionSelect
            inspections={inspections}
            loading={inspectionsLoading}
            value={v.inspection_id || ""}
            onChange={(next) => onChange("inspection_id", next)}
          />
          <p className="mt-1 text-xs text-gray-500">
            Search by the human-facing inspection number, type, site location, or date.
          </p>
        </div>
      </div>

      <div>
        <label className="text-sm text-gray-700">Description</label>
        <textarea
          value={v.description || ""}
          onChange={(e) => onChange("description", e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
          placeholder="Describe the non-conformance..."
          required
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={creating}
          className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black disabled:opacity-60"
        >
          {creating
            ? isEdit
              ? "Saving..."
              : "Creating..."
            : isEdit
              ? "Save changes"
              : "Create NCR"}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-200"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default NCRForm;
